import { useEffect, useRef, useState } from "react";
import Picture from "../Picture/Picture.jsx";
import styles from "./CompareSlider.module.css";

// The one hint, the first time the frame is in view: the handle eases out to one side, over
// to the other and back, so it reads as something to drag. [time 0-1, position %]
const HINT = [
  [0, 50],
  [0.3, 32],
  [0.72, 68],
  [1, 50],
];
const HINT_MS = 2000;
const smooth = (t) => t * t * (3 - 2 * t);
function hintAt(t) {
  for (let k = 1; k < HINT.length; k++) {
    const [t1, p1] = HINT[k];
    const [t0, p0] = HINT[k - 1];
    if (t <= t1) return p0 + (p1 - p0) * smooth((t - t0) / (t1 - t0));
  }
  return 50;
}

/**
 * Two renders from one viewpoint, `before` under `after`, split by a handle: drag anywhere on
 * the frame, or use the arrow keys (it is a range input underneath, so screen readers get a
 * slider too). `before` and `after` are { src, alt, label }, src a Picture base path.
 */
export default function CompareSlider({
  before,
  after,
  label,
  sizes = "(min-width: 80rem) 76rem, 100vw",
}) {
  const [pos, setPos] = useState(50);
  const frame = useRef(null);
  const touched = useRef(false);
  const drag = useRef(null);

  useEffect(() => {
    const el = frame.current;
    if (!el || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    let raf = 0;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        const start = performance.now();
        const step = (now) => {
          // A visitor who has already grabbed it doesn't need showing.
          if (touched.current) return;
          const t = Math.min(1, (now - start) / HINT_MS);
          setPos(hintAt(t));
          if (t < 1) raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
      },
      { threshold: 0.6 }
    );
    io.observe(el);
    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
    };
  }, []);

  const toPos = (e) => {
    const box = frame.current.getBoundingClientRect();
    return Math.max(0, Math.min(100, ((e.clientX - box.left) / box.width) * 100));
  };
  const onPointerDown = (e) => {
    if (e.button !== 0) return;
    touched.current = true;
    drag.current = e.pointerId;
    e.currentTarget.setPointerCapture(e.pointerId);
    // A finger may be starting a page scroll, so it moves the handle once it moves sideways;
    // a mouse press moves it straight away.
    if (e.pointerType !== "touch") setPos(toPos(e));
  };
  const onPointerMove = (e) => {
    if (drag.current === e.pointerId) setPos(toPos(e));
  };
  const onPointerEnd = (e) => {
    if (drag.current === e.pointerId) drag.current = null;
  };

  const shown = Math.round(pos);
  return (
    <div
      ref={frame}
      className={styles.frame}
      style={{ "--pos": `${pos}%` }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
    >
      <Picture
        src={before.src}
        alt={before.alt}
        sizes={sizes}
        className={styles.layer}
        fallbackWidth={960}
      />
      <Picture
        src={after.src}
        alt={after.alt}
        sizes={sizes}
        className={`${styles.layer} ${styles.after}`}
        fallbackWidth={960}
      />
      <span className={styles.divider} aria-hidden="true" />
      <span className={styles.handle} aria-hidden="true">
        <svg viewBox="0 0 24 24" width="22" height="22" focusable="false">
          <path d="M9 6l-6 6 6 6M15 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2" />
        </svg>
      </span>
      {/* Each side's name, gone once its side is too narrow to hold it. */}
      <span
        className={`${styles.tag} ${styles.tagBefore}`}
        data-hidden={pos < 16 ? "" : undefined}
        aria-hidden="true"
      >
        {before.label}
      </span>
      <span
        className={`${styles.tag} ${styles.tagAfter}`}
        data-hidden={pos > 84 ? "" : undefined}
        aria-hidden="true"
      >
        {after.label}
      </span>
      <input
        className={styles.range}
        type="range"
        min="0"
        max="100"
        step="1"
        value={shown}
        aria-label={label}
        aria-valuetext={`${shown}% ${before.label.toLowerCase()}, ${100 - shown}% ${after.label.toLowerCase()}`}
        onChange={(e) => {
          touched.current = true;
          setPos(Number(e.target.value));
        }}
      />
    </div>
  );
}
